import React from 'react'
import getConfig from 'next/config'
import styled from '@emotion/styled'
import { space, Button, Select, device, color, Text } from '@ticketswap/solar'
import cookies from 'js-cookie'
import getUrlFromLanguage from '~/utils/i18n/getUrlFromLanguage'
import { track } from '~/utils'
import Router from 'next/router'
import {
  LOCALE_BAR_VIEWED,
  LOCALE_BAR_DISMISSED,
  LOCALE_BAR_SET,
} from '~/lib/tracking-events'
import { useViewer } from '~/components/ViewerContext'
import getSupportedBrowserLanguage from '~/utils/getSupportedBrowserLanguage'
import { CloseRounded } from '@ticketswap/comets'
import { useMutation } from '@apollo/react-hooks'
import CHANGE_USER_LOCALE from '~/graphql/mutations/changeUserLocale'

const { LOCALE_NOTIFICATION_COOKIE_NAME } = getConfig().publicRuntimeConfig

const titles = {
  de: 'Browse TicketSwap auf:',
  en: 'Browse TicketSwap in:',
  nl: 'Bekijk TicketSwap in het:',
  fr: 'Afficher TicketSwap en:',
  es: 'Navega por TicketSwap en:',
  hu: 'Keresés nyelve TicketSwapnál:',
}

const buttonLabels = {
  de: 'Sprache ändern',
  en: 'Change language',
  nl: 'Verander taal',
  fr: 'Changer la langue',
  es: 'Cambiar el idioma',
  hu: 'Áltoztassa meg a nyelvet',
}

const shortButtonLabels = {
  de: 'Ändern',
  en: 'Change',
  nl: 'Verander',
  fr: 'Changer',
  es: 'Cambiar',
  hu: 'Változás',
}

const languages = {
  en: [
    { value: 'en', name: 'English' },
    { value: 'de-DE', name: 'German' },
    { value: 'fr-FR', name: 'French' },
    { value: 'es-ES', name: 'Spanish' },
    { value: 'hu-HU', name: 'Hungarian' },
    { value: 'nl-NL', name: 'Dutch' },
  ],
  de: [
    { value: 'en', name: 'Englisch' },
    { value: 'de-DE', name: 'Deutsch' },
    { value: 'fr-FR', name: 'Französisch' },
    { value: 'es-ES', name: 'Spanisch' },
    { value: 'hu-HU', name: 'Ungarisch' },
    { value: 'nl-NL', name: 'Holländisch' },
  ],
  fr: [
    { value: 'en', name: 'Anglais' },
    { value: 'de-DE', name: 'Allemand' },
    { value: 'fr-FR', name: 'Français' },
    { value: 'es-ES', name: 'Espagnol' },
    { value: 'hu-HU', name: 'Hongrois' },
    { value: 'nl-NL', name: 'Hollandais' },
  ],
  es: [
    { value: 'en', name: 'Inglés' },
    { value: 'de-DE', name: 'Alemán' },
    { value: 'fr-FR', name: 'Francés' },
    { value: 'es-ES', name: 'Español' },
    { value: 'hu-HU', name: 'Húngaro' },
    { value: 'nl-NL', name: 'Holandés' },
  ],
  hu: [
    { value: 'en', name: 'Angol' },
    { value: 'de-DE', name: 'Német' },
    { value: 'fr-FR', name: 'Francia' },
    { value: 'es-ES', name: 'Spanyol' },
    { value: 'hu-HU', name: 'Húngaro' },
    { value: 'nl-NL', name: 'Magyar' },
  ],
  nl: [
    { value: 'en', name: 'Engels' },
    { value: 'de-DE', name: 'Duits' },
    { value: 'fr-FR', name: 'Frans' },
    { value: 'es-ES', name: 'Spaans' },
    { value: 'hu-HU', name: 'Hongaars' },
    { value: 'nl-NL', name: 'Nederlands' },
  ],
}

const getTranslations = code => {
  switch (code) {
    case 'en':
    case 'en-GB':
    case 'en-US':
    case 'en-IE':
      return {
        title: titles.en,
        languages: languages.en.map(({ value, name }) => ({
          name,
          value: value === 'en' ? code : value,
        })),
        buttonLabel: buttonLabels.en,
        shortButtonLabel: shortButtonLabels.en,
      }
    case 'de-DE':
    case 'de-AT':
    case 'de-CH':
      return {
        title: titles.de,
        languages: languages.de.map(({ value, name }) => ({
          name,
          value: value === 'de-DE' ? code : value,
        })),
        buttonLabel: buttonLabels.de,
        shortButtonLabel: shortButtonLabels.de,
      }
    case 'fr-FR':
    case 'fr-BE':
    case 'fr-CH':
      return {
        title: titles.fr,
        languages: languages.fr.map(({ value, name }) => ({
          name,
          value: value === 'fr-FR' ? code : value,
        })),
        buttonLabel: buttonLabels.fr,
        shortButtonLabel: shortButtonLabels.fr,
      }
    case 'hu-HU':
      return {
        title: titles.hu,
        languages: languages.hu.map(({ value, name }) => ({
          name,
          value: value === 'hu-HU' ? code : value,
        })),
        buttonLabel: buttonLabels.hu,
        shortButtonLabel: shortButtonLabels.hu,
      }
    case 'es-ES':
      return {
        title: titles.es,
        languages: languages.es.map(({ value, name }) => ({
          name,
          value: value === 'es-ES' ? code : value,
        })),
        buttonLabel: buttonLabels.es,
        shortButtonLabel: shortButtonLabels.es,
      }
    case 'nl-NL':
    case 'nl-BE':
      return {
        title: titles.nl,
        languages: languages.nl.map(({ value, name }) => ({
          name,
          value: value === 'nl-NL' ? code : value,
        })),
        buttonLabel: buttonLabels.nl,
        shortButtonLabel: shortButtonLabels.nl,
      }
    default:
      return {
        title: titles.en,
        languages: languages.en,
        buttonLabel: buttonLabels.en,
        shortButtonLabel: shortButtonLabels.en,
      }
  }
}

const Container = styled.div`
  background-color: white;
  padding: ${space[16]};
  z-index: 6;
  position: relative;

  @media ${device.tablet} {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  margin-top: ${space[8]};

  @media ${device.tablet} {
    margin: 0 ${space[16]};
  }

  div[role='listbox'] {
    /* Prevent clipping of language select menu */
    z-index: 4;
  }
`

const StyledSelect = styled(Select)`
  height: ${space[44]};
`

const StyledButton = styled(Button)`
  margin-left: ${space[16]};
`

const ButtonLabel = styled.span`
  display: ${props => (props.size === 'sm' ? 'block' : 'none')};

  @media ${device.tablet} {
    display: ${props => (props.size === 'sm' ? 'none' : 'block')};
  }
`

const CloseButton = styled.button`
  margin-left: ${space[16]};
  color: ${color.spaceLighter};
`

function LocaleBar(props) {
  const { viewer } = useViewer()
  const [changeUserLocale, { loading: changingLocale }] = useMutation(
    CHANGE_USER_LOCALE
  )
  const systemLanguage = props.language
  const browserLanguage = getSupportedBrowserLanguage()
  const userLanguage =
    viewer && viewer.locale ? viewer.locale.split('_')[0] : null
  const browserLanguageContainsSystemLanguage =
    browserLanguage.indexOf(systemLanguage) !== -1 // Cover dialects
  const cookie = cookies.get(LOCALE_NOTIFICATION_COOKIE_NAME)
  const initialShow =
    browserLanguage !== systemLanguage &&
    userLanguage !== systemLanguage &&
    !browserLanguageContainsSystemLanguage &&
    cookie !== '1'
  const [show, setShow] = React.useState(initialShow)
  const { title, languages, buttonLabel, shortButtonLabel } = getTranslations(
    browserLanguage
  )
  const [activeLanguage, setActiveLanguage] = React.useState(browserLanguage)

  Router.events.on('routeChangeStart', () => {
    // Hide locale-bar on subsequent page-views
    if (cookies.get(LOCALE_NOTIFICATION_COOKIE_NAME) !== '1') {
      cookies.set(LOCALE_NOTIFICATION_COOKIE_NAME, 1)
    }
  })

  React.useEffect(() => {
    if (show && cookie !== '1') {
      track(LOCALE_BAR_VIEWED)
    }

    if (show && cookie === '1') {
      setShow(false)
    }

    return function cleanup() {
      // Set cookie on unmount (e.g. if a user refreshes the page)
      cookies.set(LOCALE_NOTIFICATION_COOKIE_NAME, 1)
    }
  }, [show, cookie])

  if (!show) return null

  return (
    <Container>
      <Text>{title}</Text>
      <ButtonGroup>
        <StyledSelect
          id="language"
          label="Language"
          hideLabel
          items={languages}
          initialSelectedItem={
            languages.filter(lang => lang.value === browserLanguage)[0]
          }
          onChange={lang => setActiveLanguage(lang.value)}
        />
        <StyledButton
          size="small"
          loading={changingLocale}
          onClick={async () => {
            if (viewer) {
              await changeUserLocale({
                variables: {
                  input: {
                    locale: activeLanguage,
                  },
                },
              })
            }
            track(LOCALE_BAR_SET)
            window.location.href = `${getUrlFromLanguage(
              activeLanguage
            )}?language=${activeLanguage}`
          }}
        >
          <ButtonLabel size="lg">{buttonLabel}</ButtonLabel>
          <ButtonLabel size="sm">{shortButtonLabel}</ButtonLabel>
        </StyledButton>
        <CloseButton
          onClick={() => {
            setShow(false)
            track(LOCALE_BAR_DISMISSED)
          }}
        >
          <CloseRounded size={21} />
        </CloseButton>
      </ButtonGroup>
    </Container>
  )
}

export default LocaleBar
